import React from "react";

/**
 * Custom certification seal badges — designed to convey trust
 * without infringing on official trademarked logos.
 * Circular seal style with brand-safe visual language.
 */

const SEAL_COLORS = {
  gmp: { ring: "#1E7A56", accent: "#34D8A0", text: "#0A2540" },
  who: { ring: "#0A76A8", accent: "#4FB4E3", text: "#0A2540" },
  fda: { ring: "#B23A3A", accent: "#E75757", text: "#0A2540" },
  fssai: { ring: "#8E5F17", accent: "#F3A93A", text: "#0A2540" },
};

function Seal({ variant, abbr, top = "CERTIFIED", bottom = "STANDARD", tick }) {
  const c = SEAL_COLORS[variant];
  return (
    <svg viewBox="0 0 220 220" className="h-full w-full" role="img" aria-label={`${abbr} ${top}`}>
      <defs>
        <path id={`arc-top-${variant}`} d="M 30 110 A 80 80 0 0 1 190 110" fill="none" />
        <path id={`arc-bot-${variant}`} d="M 32 110 A 78 78 0 0 0 188 110" fill="none" />
      </defs>

      {/* Outer ring */}
      <circle cx="110" cy="110" r="98" fill="#ffffff" stroke={c.ring} strokeWidth="6" />
      {/* Inner double ring */}
      <circle cx="110" cy="110" r="86" fill="none" stroke={c.ring} strokeWidth="1.5" />
      <circle cx="110" cy="110" r="60" fill={c.accent} opacity="0.08" />

      {/* Notches around edge (14 evenly-spaced ticks) */}
      {Array.from({ length: 32 }).map((_, i) => {
        const deg = (i / 32) * 360;
        const rad = (deg * Math.PI) / 180;
        const x1 = 110 + Math.cos(rad) * 92;
        const y1 = 110 + Math.sin(rad) * 92;
        const x2 = 110 + Math.cos(rad) * 96;
        const y2 = 110 + Math.sin(rad) * 96;
        return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={c.ring} strokeWidth="1.5" />;
      })}

      {/* Arc text — top */}
      <text fill={c.ring} fontFamily="Arial, sans-serif" fontSize="14" fontWeight="700" letterSpacing="4">
        <textPath href={`#arc-top-${variant}`} startOffset="50%" textAnchor="middle">
          {top}
        </textPath>
      </text>

      {/* Arc text — bottom (mirrored, uses same arc but rotate group) */}
      <g transform="rotate(180 110 110)">
        <text fill={c.ring} fontFamily="Arial, sans-serif" fontSize="12" fontWeight="700" letterSpacing="3">
          <textPath href={`#arc-top-${variant}`} startOffset="50%" textAnchor="middle">
            {bottom}
          </textPath>
        </text>
      </g>

      {/* Center emblem */}
      {tick && (
        <path
          d="M85 108 L104 128 L138 88"
          stroke={c.ring}
          strokeWidth="6"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      )}
      {/* Big abbreviation */}
      <text
        x="110"
        y={tick ? 158 : 122}
        textAnchor="middle"
        fill={c.text}
        fontFamily="Arial Black, Arial, sans-serif"
        fontSize={tick ? 22 : 40}
        fontWeight="900"
        letterSpacing="1.5"
      >
        {abbr}
      </text>

      {/* Divider dots at 3 and 9 o'clock */}
      <circle cx="14" cy="110" r="3" fill={c.ring} />
      <circle cx="206" cy="110" r="3" fill={c.ring} />
    </svg>
  );
}

export const GmpSeal = () => <Seal variant="gmp" abbr="GMP" top="GOOD MFG PRACTICES" bottom="CERTIFIED" tick />;
export const WhoGmpSeal = () => <Seal variant="who" abbr="WHO-GMP" top="INTERNATIONAL" bottom="COMPLIANCE" tick />;
export const UsfdaSeal = () => <Seal variant="fda" abbr="USFDA" top="US FOOD & DRUG" bottom="REGISTERED FACILITY" tick />;
export const FssaiSeal = () => <Seal variant="fssai" abbr="FSSAI" top="FOOD SAFETY & STANDARDS" bottom="AUTHORITY OF INDIA" tick />;
